import { useId } from '@chakra-ui/react';
import { useFormik } from 'formik';
import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { object, string } from 'yup';

import { COUNTRIES, COUNTRY_REGIONS } from '@frontend/addresses';
import {
  Button,
  DrawerBody,
  DrawerButtonGroup,
  DrawerFooter,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
} from '@frontend/components';

import {
  CountrySelect,
  RegionSelect,
  type CountryRegionData,
} from 'modules/common/components/addressFormFields';

import { getDefaultFacilityCountry } from 'store/session/selectors';

import { FormikHooksReactSelectWrapper } from 'utils/FormikHooksReactSelectWrapper';

import { billingAddressDrawer, save as performSave } from './data/actions';
import { getBillingAddress, getIsSaving } from './data/reducer';

type BillingAddressFormData = {
  address1: string;
  address2: string;
  city: string;
  country: string;
  postalCode: string;
  state: string;
};

export const BillingAddressForm = () => {
  const billingAddress = useSelector(getBillingAddress);
  const isSaving = useSelector(getIsSaving);
  const dispatch = useDispatch();

  const close = useCallback(() => {
    dispatch(billingAddressDrawer.close());
  }, [dispatch]);

  const save = useCallback(
    (addressData: any) => {
      dispatch(performSave.request(addressData));
    },
    [dispatch],
  );

  const defaultCountry = useSelector(getDefaultFacilityCountry);

  const formId = useId(undefined, 'billing-address-create-form');
  const {
    errors,
    values,
    touched,
    handleBlur,
    handleChange,
    handleSubmit,
    setFieldTouched,
    setFieldValue,
  } = useFormik<BillingAddressFormData>({
    initialValues: {
      address1: billingAddress?.address?.address1 ?? '',
      address2: billingAddress?.address?.address2 ?? '',
      city: billingAddress?.address?.city ?? '',
      country: billingAddress?.address?.country?.abbr ?? defaultCountry.abbr,
      postalCode: billingAddress?.address?.postalCode ?? '',
      state: billingAddress?.address?.state?.abbr ?? '',
    },
    validationSchema: object().shape({
      address1: string().required('This field is required'),
      address2: string().nullable(),
      city: string().required('This field is required'),
      country: string()
        .required('This field is required')
        .oneOf(
          COUNTRIES.map((country) => country.abbreviation),
          'Please enter a valid country',
        )
        .required('This field is required'),
      postalCode: string().nullable(),
      state: string()
        .nullable()
        .test('country-covalidation', 'Invalid value', (value, context) => {
          if (value) {
            const selectedCountry = context.parent.country;
            const countryRegionData = (
              COUNTRY_REGIONS as Record<string, CountryRegionData>
            )[selectedCountry];
            if (countryRegionData) {
              return countryRegionData.regions.some(
                (region) => value === region.abbreviation,
              );
            }
          }
          return true;
        })
        .test(
          'address-covalidation',
          'This field is required',
          (value, context) => {
            const selectedCountry = context.parent.country;
            const availableRegions =
              (COUNTRY_REGIONS as Record<string, CountryRegionData>)[
                selectedCountry
              ]?.regions ?? [];

            return !!value || !availableRegions.length;
          },
        ),
    }),
    onSubmit: (formData) => {
      save(formData);
    },
    validateOnChange: false,
    validateOnBlur: true,
  });

  return (
    <>
      <DrawerBody
        as="form"
        display="flex"
        flexDir="column"
        gap={2}
        onSubmit={handleSubmit}
        autoComplete="off"
        id={formId}
      >
        <FormControl
          isRequired
          isInvalid={!!errors.address1 && touched.address1}
          id="address1"
        >
          <FormLabel>Address 1</FormLabel>
          <Input
            autoComplete="new-password"
            name="address1"
            onChange={handleChange}
            onBlur={handleBlur}
            value={values.address1}
          />
          <FormErrorMessage>{errors.address1}</FormErrorMessage>
        </FormControl>
        <FormControl
          isInvalid={!!errors.address2 && touched.address2}
          id="address2"
        >
          <FormLabel>Address 2</FormLabel>
          <Input
            autoComplete="new-password"
            name="address2"
            onChange={handleChange}
            onBlur={handleBlur}
            value={values.address2}
          />
          <FormErrorMessage>{errors.address2}</FormErrorMessage>
        </FormControl>
        <Flex gap={1.5}>
          <FormControl
            isRequired
            isInvalid={!!errors.city && touched.city}
            id="city"
          >
            <FormLabel>City</FormLabel>
            <Input
              autoComplete="new-password"
              name="city"
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.city}
            />
            <FormErrorMessage>{errors.city}</FormErrorMessage>
          </FormControl>
          <FormControl isInvalid={!!errors.state && touched.state} id="state">
            <FormLabel>
              {(COUNTRY_REGIONS as Record<string, CountryRegionData>)[
                values.country
              ]?.regionLabel ?? 'State/Province'}
            </FormLabel>
            <FormikHooksReactSelectWrapper
              name="state"
              setFieldTouched={setFieldTouched}
              setFieldValue={setFieldValue}
              values={values}
            >
              <RegionSelect country={values.country} />
            </FormikHooksReactSelectWrapper>
            <FormErrorMessage>{errors.state}</FormErrorMessage>
          </FormControl>
        </Flex>
        <Flex gap={1.5}>
          <FormControl
            isInvalid={!!errors.postalCode && touched.postalCode}
            id="postalCode"
          >
            <FormLabel>
              {(COUNTRY_REGIONS as Record<string, CountryRegionData>)[
                values.country
              ]?.postalCodeLabel ?? 'Postal Code'}
            </FormLabel>
            <Input
              autoComplete="new-password"
              name="postalCode"
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.postalCode}
            />
            <FormErrorMessage>{errors.postalCode}</FormErrorMessage>
          </FormControl>
          <FormControl
            isRequired
            isInvalid={!!errors.country && touched.country}
            id="country"
          >
            <FormLabel>Country</FormLabel>
            <FormikHooksReactSelectWrapper
              name="country"
              setFieldTouched={setFieldTouched}
              setFieldValue={setFieldValue}
              values={values}
            >
              <CountrySelect
                onChange={() => {
                  setFieldValue('state', '');
                  setFieldValue('postalCode', '');
                }}
              />
            </FormikHooksReactSelectWrapper>
            <FormErrorMessage>{errors.country}</FormErrorMessage>
          </FormControl>
        </Flex>
      </DrawerBody>
      <DrawerFooter>
        <DrawerButtonGroup>
          <Button variant="secondary" isLoading={isSaving} onClick={close}>
            Cancel
          </Button>
          <Button isLoading={isSaving} type="submit" form={formId}>
            Save
          </Button>
        </DrawerButtonGroup>
      </DrawerFooter>
    </>
  );
};
